/**
 * DossierBackup — Batch #14.339 : sauvegarde / restauration d'un seul dossier client (Master).
 * Choix du dossier → « Sauvegarder ce dossier » → liste de ses archives dans Drive › Dossiers (télécharger / restaurer).
 */
import { useEffect, useState } from "react";
import { api, getApiBase, authToken } from "@/api";
import { toast } from "@/lib/toast";
import { Download, FolderArchive, History, Loader2, RefreshCw } from "lucide-react";
import RestoreModal from "./RestoreModal";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const mb = (n) => `${(n / 1048576).toFixed(1)} Mo`;

export default function DossierBackup({ settings, onChanged }) {
  const [dossiers, setDossiers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => { api.get("/admin/backup/dossiers").then((r) => setDossiers(r.data.items || [])).catch(() => {}); }, []);
  const loadList = async (mid) => {
    if (!mid) { setItems(null); return; }
    try { const r = await api.get(`/admin/backup/dossier/drive?master_id=${mid}`); setItems(r.data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Drive injoignable"); setItems([]); }
  };
  useEffect(() => { loadList(sel); }, [sel]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/backup/dossier/${sel}/run`, {}, { timeout: 300000 });
      toast.success(`Dossier sauvegardé : ${r.data.documents} documents (${mb(r.data.size)}) → Drive › Dossiers`);
      loadList(sel); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Sauvegarde du dossier impossible"); }
    finally { setBusy(false); }
  };
  const download = async (it) => {
    setDl(it.file_id);
    try {
      const resp = await fetch(`${getApiBase()}/admin/backup/drive/${it.file_id}/download`, { headers: { Authorization: `Bearer ${authToken()}` } });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).detail || "Téléchargement impossible");
      const url = URL.createObjectURL(await resp.blob());
      const a = document.createElement("a"); a.href = url; a.download = it.name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setDl(null); }
  };

  const filtered = dossiers.filter((d) => !q || `${d.name} ${d.client_number}`.toLowerCase().includes(q.toLowerCase())).slice(0, 200);
  const current = dossiers.find((d) => d.id === sel);
  return (
    <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="backup-dossier">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><FolderArchive className="w-4 h-4 text-indigo-600" /> Sauvegarde par dossier client</h3>
      <p className="text-xs text-slate-500">Archive chiffrée (même mot de passe) d'un seul dossier : sa fiche, ses membres et toutes ses données (comptabilité, paie, fiscal, immeubles, documents…). Restaurer un dossier ne touche pas aux autres. Les 10 dernières archives par dossier sont conservées dans Drive › Sauvegardes › Dossiers.</p>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-xs text-slate-600 flex-1 min-w-[16rem]">Dossier
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un dossier (nom ou n° client)…" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-1" data-testid="backup-dossier-search" />
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="backup-dossier-select">
            <option value="">— Choisir un dossier —</option>
            {filtered.map((d) => <option key={d.id} value={d.id}>{d.client_number ? `${d.client_number} · ` : ""}{d.name}{d.archived ? " (archivé)" : ""}</option>)}
          </select>
        </label>
        <button onClick={run} disabled={!sel || busy || !settings?.password_set || !settings?.drive_configured}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50" data-testid="backup-dossier-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderArchive className="w-4 h-4" />} {busy ? "Sauvegarde…" : "Sauvegarder ce dossier"}
        </button>
      </div>
      {sel && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-600">Archives de <b>{current?.name}</b> ({items ? items.length : "…"})</div>
            <button onClick={() => loadList(sel)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="backup-dossier-refresh"><RefreshCw className="w-3 h-3" /> Actualiser</button>
          </div>
          {items && !items.length && <div className="text-xs text-slate-500">Aucune archive pour ce dossier.</div>}
          {items && items.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1.5 pr-2">Fichier</th><th className="py-1.5 pr-2">Créée</th><th className="py-1.5 pr-2 text-right">Taille</th><th className="py-1.5 text-right">Actions</th></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.file_id} className="border-b last:border-0" data-testid={`backup-dossier-row-${it.file_id}`}>
                    <td className="py-1.5 pr-2 font-mono text-xs">{it.name}</td>
                    <td className="py-1.5 pr-2 text-xs">{fmt(it.created_at)}</td>
                    <td className="py-1.5 pr-2 text-right text-xs">{mb(it.size)}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => download(it)} disabled={dl === it.file_id} className="p-1 text-slate-500 hover:text-indigo-700" title="Télécharger" data-testid={`backup-dossier-download-${it.file_id}`}>{dl === it.file_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}</button>
                      <button onClick={() => setRestoring(it)} className="p-1 text-slate-500 hover:text-rose-700" title="Restaurer ce dossier" data-testid={`backup-dossier-restore-${it.file_id}`}><History className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {restoring && <RestoreModal scope="dossier" item={restoring} onClose={() => setRestoring(null)} onDone={() => { setRestoring(null); loadList(sel); onChanged?.(); }} />}
    </div>
  );
}
